import React, { useState } from 'react';
import { Box, Typography, Grid, Button, Paper, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import InvestorContextForm from './InvestorContextForm';
import { Portfolio } from '../../types/Portfolio';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface InvestorContextPanelProps {
  investorContext: Portfolio['investorContext'];
  onSave: (data: any) => Promise<void>;
}

const InvestorContextPanel: React.FC<InvestorContextPanelProps> = ({
  investorContext,
  onSave,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [data, setData] = useState(investorContext || {});
  const [expanded, setExpanded] = useState(false); // Changed to false - CLOSED BY DEFAULT

  const handleSave = async (updatedData: any) => {
    await onSave(updatedData);
    setData(updatedData);
    setEditMode(false);
    setExpanded(false); // Close after saving
  };

  const handleEditClick = () => {
    if (editMode) {
      // Cancel - close accordion
      setEditMode(false);
      setExpanded(false);
    } else {
      // Edit - open accordion
      setEditMode(true);
      setExpanded(true);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: '#2C3E50',
        color: '#FFFFFF',
        px: { xs: 3, md: 5 },
        py: 2,
        borderBottom: '1px solid #3A9188',
      }}
    >
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
        disableGutters
        sx={{
          bgcolor: '#2C3E50',
          color: '#FFFFFF',
          border: '1px solid #3A9188',
          borderRadius: 2,
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: '0 !important' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: '#3A9188' }} />}
          sx={{
            borderBottom: '1px solid #3A9188',
            '& .MuiAccordionSummary-content': { my: 1 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: '#3A9188' }}
            >
              Investor Context (Internal)
            </Typography>

            <Button
              variant="outlined"
              onClick={handleEditClick}
              sx={{
                borderColor: '#3A9188',
                color: '#3A9188',
                fontWeight: 600,
                '&:hover': { borderColor: '#2E736A', color: '#2E736A' },
              }}
            >
              {editMode ? 'Cancel' : 'Edit'}
            </Button>
          </Box>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            bgcolor: '#34495E',
            borderTop: '1px solid #3A9188',
            px: { xs: 2, md: 4 },
            py: 3,
          }}
        >
          {editMode ? (
            <InvestorContextForm investorContext={data} onSave={handleSave} />
          ) : (
            <>
              {data ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        bgcolor: '#2C3E50',
                        border: '1px solid #3A9188',
                        p: 2,
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{ color: '#A4B0BE', fontSize: '0.8rem' }}
                      >
                        Target Investor
                      </Typography>
                      <Typography sx={{ fontWeight: 600 }}>
                        {data.targetInvestor || '—'}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        bgcolor: '#2C3E50',
                        border: '1px solid #3A9188',
                        p: 2,
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{ color: '#A4B0BE', fontSize: '0.8rem' }}
                      >
                        Funding Type
                      </Typography>
                      <Typography sx={{ fontWeight: 600 }}>
                        {Array.isArray(data.fundingType)
                          ? data.fundingType.join(', ')
                          : data.fundingType || '—'}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        bgcolor: '#2C3E50',
                        border: '1px solid #3A9188',
                        p: 2,
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{ color: '#A4B0BE', fontSize: '0.8rem' }}
                      >
                        Prepared For
                      </Typography>
                      <Typography sx={{ fontWeight: 600 }}>
                        {data.contactName || '—'}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        bgcolor: '#2C3E50',
                        border: '1px solid #3A9188',
                        p: 2,
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{ color: '#A4B0BE', fontSize: '0.8rem' }}
                      >
                        Contact Email
                      </Typography>
                      <Typography sx={{ fontWeight: 600 }}>
                        {data.contactEmail || '—'}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        bgcolor: '#2C3E50',
                        border: '1px solid #3A9188',
                        p: 2,
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{ color: '#A4B0BE', fontSize: '0.8rem' }}
                      >
                        Priority
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color:
                            data.priority === 'high'
                              ? '#E74C3C'
                              : data.priority === 'medium'
                              ? '#F1C40F'
                              : data.priority === 'low'
                              ? '#3A9188'
                              : '#FFFFFF',
                        }}
                      >
                        {data.priority
                          ? data.priority.charAt(0).toUpperCase() +
                            data.priority.slice(1)
                          : '—'}
                      </Typography>
                    </Paper>
                  </Grid>

                  {data.objectives && data.objectives.length > 0 && (
                    <Grid item xs={12}>
                      <Paper
                        elevation={0}
                        sx={{
                          bgcolor: '#2C3E50',
                          border: '1px solid #3A9188',
                          p: 2,
                        }}
                      >
                        <Typography
                          variant="overline"
                          sx={{ color: '#A4B0BE', fontSize: '0.8rem' }}
                        >
                          Objectives
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                          {data.objectives.map((o: string, i: number) => (
                            <li key={i}>{o}</li>
                          ))}
                        </ul>
                      </Paper>
                    </Grid>
                  )}

                  {data.similarSponsorships && data.similarSponsorships.length > 0 && (
                    <Grid item xs={12}>
                      <Paper
                        elevation={0}
                        sx={{
                          bgcolor: '#2C3E50',
                          border: '1px solid #3A9188',
                          p: 2,
                        }}
                      >
                        <Typography
                          variant="overline"
                          sx={{ color: '#A4B0BE', fontSize: '0.8rem' }}
                        >
                          Similar Sponsorships
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                          {data.similarSponsorships.map((s: string, i: number) => (
                            <li key={i}>{s}</li>
                          ))}
                        </ul>
                      </Paper>
                    </Grid>
                  )}

                  {data.notes && (
                    <Grid item xs={12}>
                      <Paper
                        elevation={0}
                        sx={{
                          bgcolor: '#2C3E50',
                          border: '1px solid #3A9188',
                          p: 2,
                        }}
                      >
                        <Typography
                          variant="overline"
                          sx={{ color: '#A4B0BE', fontSize: '0.8rem' }}
                        >
                          Notes
                        </Typography>
                        <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                          {data.notes}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Typography sx={{ mt: 2, color: '#A4B0BE' }}>
                  No investor context data yet. Click "Edit" to add details.
                </Typography>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default InvestorContextPanel;